import { motion } from "framer-motion";
import { Building2, Sparkles, CheckCircle, Phone, Clock, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { siteConfig } from "@/lib/site-config";
import { locations } from "@/lib/locations-data";
import { pageHeroes } from "@/lib/page-heroes";
import { HERO_SIZES, HERO_WIDTHS, srcSetFor } from "@/lib/responsive-image";
import FormEmbed from "@/components/FormEmbed";

/** The LCP element for the whole site; vite.config.ts preloads it from here. */
const heroImage = pageHeroes["/"];

const badges = [
  { icon: Building2, label: "Registered Florida LLC" },
  { icon: Home, label: "Homes & Businesses" },
  { icon: Clock, label: "Open 7 Days" },
  { icon: CheckCircle, label: "Free Estimates" },
];

const stats = [
  { value: "7", label: "Days a Week", suffix: "/7" },
  { value: String(locations.length), label: "Areas Served", suffix: "+" },
  { value: "14", label: "Hours a Day", suffix: "hr" },
];

const HeroSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section
      id="home"
      className="relative min-h-[100svh] flex items-center overflow-hidden"
    >
      <img
        src={heroImage}
        srcSet={srcSetFor(heroImage, HERO_WIDTHS)}
        sizes={HERO_SIZES}
        alt="Professional cleaning service in Orange Park, Florida"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        width={1920}
        height={1080}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(213,62%,9%/0.82)] via-[hsl(213,62%,9%/0.7)] to-[hsl(40,45%,20%/0.45)]" />
      <div className="absolute inset-0 gradient-mesh opacity-40" />

      <motion.div
        className="absolute top-20 right-20 w-96 h-96 rounded-full opacity-25 hidden md:block"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)" }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.18, 0.3, 0.18] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-40 left-10 w-64 h-64 rounded-full opacity-20 hidden md:block"
        style={{ background: "radial-gradient(circle, hsl(var(--secondary) / 0.4), transparent 70%)" }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.12, 0.25, 0.12] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      <div className="relative z-10 container-max px-4 pt-20 pb-32 md:pt-24 md:pb-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15, duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs sm:text-sm font-semibold px-4 sm:px-5 py-2 rounded-full mb-6 shadow-lg"
              >
                <Sparkles className="h-4 w-4 text-secondary" />
                <span className="text-white">Orange Park & Greater Jacksonville · Open 7 Days</span>
              </motion.div>

              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] mb-5 tracking-tight font-bold drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]">
                Spotless Homes.
                <br />
                <span className="text-gradient-on-dark">Polished</span> Businesses.
              </h1>

              {/* Readable tagline with glass background card */}
              <div className="inline-block bg-[#0a172b]/55 backdrop-blur-md border border-white/15 rounded-2xl px-5 py-4 mb-8 max-w-xl shadow-xl">
                <p className="text-white text-base md:text-lg leading-relaxed">
                  <span className="font-bold text-white">Liz Luxes Services Cleaning</span> delivers
                  detail-driven deep cleaning, residential and commercial cleaning, move in/out
                  service, Airbnb turnovers, laundry, and organization across Clay, Duval &
                  St. Johns counties — 7 days a week, 7 AM – 9 PM.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="text-base gap-2 px-7 py-6 rounded-xl font-semibold shadow-xl"
                >
                  Get Free Estimate
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="text-base gap-2 px-7 py-6 rounded-xl border-2 border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm font-semibold"
                >
                  <a href={siteConfig.phoneHref}>
                    <Phone className="h-5 w-5" /> {siteConfig.phone}
                  </a>
                </Button>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="glass-card rounded-xl px-5 py-3 inline-flex items-center gap-3"
              >
                <span className="text-2xl">🎁</span>
                <span className="text-white font-semibold text-sm">
                  <span className="text-secondary font-bold">Discounts</span> for recurring customers & referrals
                </span>
              </motion.div>
            </motion.div>
          </div>

          {/* Form widget — exactly as currently positioned, right-side hero, original dimensions preserved */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="widget-shell bg-white">
              <div className="px-6 pt-6 pb-2">
                <h2 className="font-heading text-2xl text-foreground font-bold tracking-tight">
                  Get Your Free Estimate
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  No pressure, no obligation — just honest pricing.
                </p>
              </div>
              <FormEmbed className="p-4 sm:p-6" />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-10 lg:mt-12 glass-card rounded-2xl p-5 md:p-6 gradient-border"
        >
          <div className="flex flex-wrap items-center justify-around gap-5">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + i * 0.15, duration: 0.5 }}
                className="text-center"
              >
                <div className="font-heading text-3xl md:text-4xl text-white tracking-wide font-bold">
                  {stat.value}<span className="text-secondary">{stat.suffix}</span>
                </div>
                <div className="text-white/90 text-xs font-medium uppercase tracking-wider mt-1">{stat.label}</div>
              </motion.div>
            ))}
            {badges.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-lg bg-secondary/25 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-secondary" />
                </div>
                <span className="text-white/85 text-xs sm:text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Concave/curved bottom edge */}
      <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-20 md:h-24 leading-none">
        <svg
          viewBox="0 0 1440 96"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="block w-full h-full"
          preserveAspectRatio="none"
        >
          <path
            d="M0 0C240 96 480 96 720 64C960 32 1200 32 1440 64V96H0V0Z"
            fill="hsl(var(--background))"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
